/**
 * Digests — page hebdomadaire (Vague 16).
 * Liste des digests hebdomadaires + viewer détaillé par direction.
 */
import { useEffect, useMemo, useState } from "react";
import { Calendar, AlertTriangle, TrendingUp, Activity, Mail, Loader2, RefreshCw, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const DIRECTION_META = {
  opportunite: { color: "#1D9E75", label: "Opportunités", icon: TrendingUp },
  menace: { color: "#EF4444", label: "Menaces", icon: AlertTriangle },
  mixte: { color: "#EF9F27", label: "Mixtes", icon: Activity },
  autre: { color: "#9CA3AF", label: "Autres", icon: Activity },
};

export default function Digests() {
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [showRecipients, setShowRecipients] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/digests/weekly");
      setList(r.data.items || []);
      if (!selectedId && r.data.items?.length) setSelectedId(r.data.items[0].id);
    } catch (e) { notify.error(fmtError(e, "Erreur")); }
    finally { setLoading(false); }
  };

  const loadDetail = async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const r = await api.get(`/workspace/digests/weekly/${id}`);
      setDetail(r.data);
    } catch (e) { notify.error(fmtError(e, "Erreur")); }
  };

  const loadRecipients = async () => {
    try {
      const r = await api.get("/workspace/digests/recipients");
      setRecipients(r.data.recipients || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadList(); loadRecipients(); }, []);
  useEffect(() => { loadDetail(selectedId); }, [selectedId]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/workspace/digests/weekly/run-now");
      notify.success(`Digest généré : ${r.data.total_briefs} briefs des 7 derniers jours.`);
      await loadList();
      if (r.data?.digest_id) setSelectedId(r.data.digest_id);
    } catch (e) { notify.error(fmtError(e, "Erreur")); }
    finally { setRunning(false); }
  };

  const addRecipient = async () => {
    const e = (newEmail || "").trim().toLowerCase();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { notify.error("Email invalide"); return; }
    const next = Array.from(new Set([...recipients, e]));
    try {
      const r = await api.put("/workspace/digests/recipients", { recipients: next });
      setRecipients(r.data.recipients || []);
      setNewEmail("");
    } catch (err) { notify.error(fmtError(err, "Erreur")); }
  };
  const removeRecipient = async (e) => {
    const next = recipients.filter((x) => x !== e);
    try {
      const r = await api.put("/workspace/digests/recipients", { recipients: next });
      setRecipients(r.data.recipients || []);
    } catch (err) { notify.error(fmtError(err, "Erreur")); }
  };

  return (
    <div className="space-y-6" data-testid="digests-root">
      {/* V23.15.36 — Doctrine 3Q · menaces/opportunités hebdo à traiter */}
      <ThreeQPageHeader endpoint="/workspace/digests/top-priority" />
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#7F77DD]"><Calendar className="w-3 h-3" /> Stratégique · Digest hebdomadaire</div>
          <h1 className="noam-display text-3xl font-bold mt-1">Digests hebdomadaires</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            Chaque vendredi 17h CET, Noam consolide les briefs des 7 derniers jours regroupés par direction (opportunité, menace, mixte).
            Le digest reste disponible ici et peut être envoyé par email aux destinataires configurés.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowRecipients(!showRecipients)} className="text-xs border border-white/10 rounded-md px-3 py-1.5 text-zinc-300 hover:border-white/20 inline-flex items-center gap-1.5" data-testid="digest-recipients-toggle">
            <Mail className="w-3 h-3" /> Destinataires ({recipients.length})
          </button>
          <button onClick={runNow} disabled={running} className="btn-primary text-xs disabled:opacity-50" data-testid="digest-run-now">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Générer maintenant
          </button>
        </div>
      </div>

      {showRecipients && (
        <div className="noam-card p-4" data-testid="digest-recipients-card">
          <div className="text-xs text-zinc-400 mb-3">
            Liste des emails qui recevront le digest. L'envoi automatique sera actif dès que <strong>RESEND_API_KEY</strong> sera configurée
            côté serveur ; en attendant, les destinataires sont enregistrés et le digest reste accessible ici.
          </div>
          <div className="flex items-center gap-2 mb-3">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
              placeholder="adresse@example.com"
              className="flex-1 bg-black/30 border border-white/10 rounded px-3 py-1.5 text-xs text-white"
              data-testid="digest-recipient-input"
            />
            <button onClick={addRecipient} className="btn-primary text-xs" data-testid="digest-recipient-add"><Plus className="w-3 h-3" /> Ajouter</button>
          </div>
          {recipients.length === 0 ? (
            <div className="text-xs text-zinc-500">Aucun destinataire — ajoutez un email pour activer l'envoi automatique.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {recipients.map((r) => (
                <div key={r} className="text-xs bg-white/[0.03] border border-white/10 rounded px-2 py-1 inline-flex items-center gap-2" data-testid={`digest-recipient-${r}`}>
                  <span className="text-zinc-200">{r}</span>
                  <button onClick={() => removeRecipient(r)} className="text-zinc-500 hover:text-red-400" title="Retirer"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-zinc-500"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Chargement…</div>
      ) : list.length === 0 ? (
        <div className="noam-card p-8 text-center" data-testid="digest-empty">
          <Calendar className="w-8 h-8 text-zinc-600 mx-auto" />
          <div className="text-sm text-zinc-300 mt-3">Aucun digest pour le moment</div>
          <p className="text-xs text-zinc-500 mt-2 max-w-md mx-auto">
            Cliquez "Générer maintenant" pour créer manuellement le digest de la semaine, ou attendez vendredi 17h CET pour la génération automatique.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-1 space-y-1" data-testid="digest-list">
            {list.map((d) => (
              <button
                key={d.id}
                onClick={() => setSelectedId(d.id)}
                className={`w-full text-left p-3 rounded-md border transition ${
                  selectedId === d.id
                    ? "bg-[#7F77DD]/10 border-[#7F77DD]/40 text-white"
                    : "bg-white/[0.02] border-white/5 text-zinc-400 hover:border-white/10"
                }`}
                data-testid={`digest-list-${d.id}`}
              >
                <div className="text-xs font-semibold text-zinc-100">Semaine du {d.week_start?.slice(0, 10)}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{d.total_briefs} briefs · {Object.values(d.counts_by_direction || {}).reduce((a, b) => a + b, 0)} classés</div>
                {d.email_sent_at && <div className="text-[10px] text-emerald-400 mt-0.5">✓ Email envoyé</div>}
              </button>
            ))}
          </div>

          <div className="md:col-span-3" data-testid="digest-detail">
            {detail ? <DigestDetailView digest={detail} /> : <div className="text-xs text-zinc-500">Sélectionnez un digest dans la liste.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function DigestDetailView({ digest }) {
  const directions = useMemo(() => ["opportunite", "menace", "mixte", "autre"], []);
  return (
    <div className="space-y-4">
      <div className="noam-card p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Semaine du</div>
            <div className="text-base font-semibold text-zinc-100">{digest.week_start?.slice(0, 10)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total briefs</div>
            <div className="text-2xl font-bold text-[#7F77DD]">{digest.total_briefs || 0}</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-3">
          {directions.map((d) => {
            const meta = DIRECTION_META[d];
            const count = digest.counts_by_direction?.[d] || 0;
            const avg = digest.averages_by_direction?.[d] || 0;
            return (
              <div key={d} className="bg-white/[0.02] border border-white/5 rounded p-2 text-center" data-testid={`digest-dir-kpi-${d}`}>
                <div className="text-[10px] uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</div>
                <div className="text-xl font-bold mt-1" style={{ color: meta.color }}>{count}</div>
                {count > 0 && <div className="text-[10px] text-zinc-500">⌀ score {avg}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {directions.map((d) => {
        const items = digest.by_direction?.[d] || [];
        if (items.length === 0) return null;
        const meta = DIRECTION_META[d];
        const Icon = meta.icon;
        return (
          <div key={d} className="noam-card p-4" data-testid={`digest-section-${d}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
              <span className="noam-overline" style={{ color: meta.color }}>{meta.label} · {items.length}</span>
            </div>
            <div className="space-y-1.5">
              {items.map((b) => (
                <div key={b.id} className="flex items-center gap-3 text-xs bg-white/[0.02] border border-white/5 rounded p-2" data-testid={`digest-brief-${b.id}`}>
                  <span className="font-bold w-8 text-center" style={{ color: meta.color }}>{b.score ?? "?"}</span>
                  <span className="flex-1 text-zinc-200 truncate" title={b.title}>{b.title}</span>
                  <span className="text-[10px] text-zinc-500">{b.source_module} · {b.horizon || "—"}</span>
                  <span className="text-[10px] text-zinc-600">{b.created_at?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
